import { useMutation, useQuery } from '@tanstack/react-query'
import { StudyTextsRes } from '@/types/studyText.type'
import { axiosInstance } from '@/lib/axios'

interface StudyTextProps {
  page: number
  limit?: number
  search?: string
  moduleId: number
}

export const getStudyTexts = async ({
  page,
  limit = 10,
  search,
  moduleId,
}: StudyTextProps) => {
  const params = { page, limit, search }
  return axiosInstance.get<StudyTextsRes>(`/module/${moduleId}/study-text`, {
    params: params,
  })
}

export const uploadStudyTextFiles = async ({
  moduleId,
  files,
}: {
  moduleId: number
  files: File[]
}) => {
  const formData = new FormData()

  // Append each file to the FormData
  files.forEach((file) => {
    formData.append('files', file)
  })

  return axiosInstance.post(`/module/${moduleId}/study-text`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const useGetStudyTexts = ({
  page,
  limit,
  search,
  moduleId,
}: StudyTextProps) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['studyText', page, limit, search, moduleId],
    queryFn: () => getStudyTexts({ page, limit, search, moduleId }),
    enabled: !!moduleId,
  })

  return {
    studyTexts: data?.data.data,
    meta: data?.data.meta,
    isLoadingStudyTexts: isLoading,
    refetchStudyTexts: refetch,
  }
}

export const useUploadStudyTextFiles = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['upload-study-text-files'],
    mutationFn: uploadStudyTextFiles,
  })

  return { uploadFilesAsync: mutateAsync, isUploading: isPending }
}
